import {
  PolicyValidationResult,
  ValidationStatus,
} from '@policyvision/contracts';
import { createContext } from 'react';

import { SectionType } from '../types/PolicyCoverage';

// Frontend only parts of ValidationStatus, adds `confirmed`
export type ConfirmedValidationStatus =
  | {
      identifier: string;
      status: 'ERROR';
      description: string;
      confirmed: boolean;
    }
  | { identifier: string; status: 'COHERENT' }
  | { identifier: string; status: 'NONE' };

// Mirrors PolicyValidationResult from contracts but uses the frontend-extended status
export type FrontendPolicyValidationResult = {
  situations: ConfirmedValidationStatus[];
  policy?: ConfirmedValidationStatus;
  amendments: ConfirmedValidationStatus[];
  missingSections?: ConfirmedValidationStatus[];
  assignment?: ConfirmedValidationStatus;
  financialData?: ConfirmedValidationStatus;
};

export const toFrontendValidationResult = (
  result: PolicyValidationResult,
): FrontendPolicyValidationResult => ({
  situations: result.situations.map((v) =>
    v.status === 'ERROR' ? { ...v, confirmed: true } : v,
  ),
  amendments: result.amendments.map((v) =>
    v.status === 'ERROR' ? { ...v, confirmed: true } : v,
  ),
  missingSections: result.missingSections?.map((v) =>
    v.status === 'ERROR' ? { ...v, confirmed: true } : v,
  ),
  assignment:
    result.assignment?.status === 'ERROR'
      ? { ...result.assignment, confirmed: true }
      : result.assignment,
  policy:
    result.policy?.status === 'ERROR'
      ? { ...result.policy, confirmed: true }
      : result.policy,
  financialData:
    result.financialData?.status === 'ERROR'
      ? { ...result.financialData, confirmed: true }
      : result.financialData,
});

export const toContractsValidationStatus = (
  v: ConfirmedValidationStatus,
): ValidationStatus | null => {
  if (v.status === 'NONE') return null;
  if (v.status === 'ERROR')
    return {
      identifier: v.identifier,
      status: 'ERROR',
      description: v.description,
    };
  return { identifier: v.identifier, status: 'COHERENT' };
};

export interface ValidationContextType {
  validations: FrontendPolicyValidationResult;
  allValidations: ConfirmedValidationStatus[];
  handleValidationChange: (
    identifier: string,
    kind: SectionType,
    validation: ConfirmedValidationStatus,
  ) => void;
  findValidation: (identifier: string) => ConfirmedValidationStatus;
}

export const ValidationContext = createContext<
  ValidationContextType | undefined
>(undefined);
